/* Xiaomi 2026 · Zoho Bridge Admin · Sprint 2C v1.0
 *
 * /xiaomi/admin/zoho-bridge
 *
 * Dipali's gauge (29-May-2026):
 * "If 5 instances are stuck 'awaiting Zoho ticket creation' on 2 June,
 *  I need to see that on 2 June morning, not at the Friday review."
 *
 * Surfaces:
 *   • 4-pill health row (bridge_configured · dept_id_set · oauth · fallback)
 *   • 4-count stat grid (open · with_ticket · awaiting · failed)
 *   • Sample table of up to 30 stuck instances · partner · benefit · zone · last error
 *   • Doctrine notes + cf_keys reference
 *   • Auto-refresh every 30s · manual refresh button
 *
 * Visual: matches .xiaomi-theme (paper + Mi-orange).
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const REFRESH_MS = 30_000;


function HealthPill({ label, ok, sub, testId }) {
  const cls = ok
    ? 'bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46]'
    : 'bg-[#fef3c7] border-[#fcd34d] text-[#92400e]';
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${cls}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <span className="text-base">{ok ? '✓' : '○'}</span>
        <span className="text-sm font-semibold">{label}</span>
      </div>
      {sub && (
        <div className="text-[11px] mt-1 opacity-80 break-all">{sub}</div>
      )}
    </div>
  );
}


function CountStat({ label, value, accent, testId }) {
  const tone = {
    teal: 'text-[#0f766e]',
    amber: 'text-[#b45309]',
    red: 'text-[#b91c1c]',
    ink: 'text-[#15110d]',
  }[accent || 'ink'];
  return (
    <div
      className="rounded-lg border border-line bg-white px-5 py-4"
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-wider text-soft">
        {label}
      </div>
      <div className={`text-3xl font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}


export default function XiaomiAdminZohoBridge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [testTicket, setTestTicket] = useState(null);
  const [testSending, setTestSending] = useState(false);
  const timerRef = useRef(null);

  const sendRoutingTest = useCallback(async () => {
    setTestSending(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/admin/routing-test-ticket`, {}, auth(),
      );
      setTestTicket(r.data);
      toast.success(`Test ticket #${r.data.ticket_number || r.data.ticket_id} created in the Xiaomi department`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not create the test ticket';
      toast.error(msg);
    } finally {
      setTestSending(false);
    }
  }, []);

  const load = useCallback(async (showToast = false) => {
    try {
      const r = await axios.get(
        `${API}/xiaomi/admin/zoho-bridge-status`, auth(),
      );
      setData(r.data);
      setLastFetched(new Date());
      if (showToast) toast.success('Bridge status refreshed');
    } catch (e) {
      toast.error('Could not load bridge status');
      // eslint-disable-next-line no-console
      console.error('zoho bridge load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(false), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 xiaomi-theme" data-testid="zoho-bridge-loading">
        Loading bridge gauge…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 xiaomi-theme" data-testid="zoho-bridge-empty">
        Could not load bridge status. Refresh to try again.
      </div>
    );
  }

  const h = data.bridge_health;
  const c = data.counts;
  const stuck = data.stuck_instances || [];
  const isGreen = h.bridge_configured;

  return (
    <div className="xiaomi-theme p-8 space-y-6" data-testid="zoho-bridge-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-3xl"
            style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}
          >
            Zoho Bridge
          </h1>
          <p className="text-sm text-soft mt-1">
            Live gauge · Sprint 2C v1.0 · Xiaomi Partner Care department
            {' · '}
            {lastFetched && (
              <span className="text-[11px]">
                refreshed {lastFetched.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href={`${API}/xiaomi/admin/zoho-modules-spec.docx`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg bg-[#1B2A4A] text-[#ff6900] text-sm font-semibold hover:opacity-90 flex items-center gap-1.5"
            data-testid="zoho-bridge-modules-spec-btn"
            title="For Aditya · every Ticket + Contact field ScaleBoard reads/writes, with picklist values and pre-launch checklist"
          >
            🧾 Zoho modules spec for Aditya (.docx)
          </a>
          <a
            href={`${API}/xiaomi/admin/team-ops-manual.docx`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg bg-[#0b9b6e] text-white text-sm font-semibold hover:opacity-90 flex items-center gap-1.5"
            data-testid="zoho-bridge-team-ops-manual-btn"
            title="The complete Xiaomi 2026 ops manual · for the whole team · roles, workflows, KPIs, escalations"
          >
            📖 Team Ops Manual (.docx)
          </a>
          <a
            href={`${API}/xiaomi/admin/zoho-macro-walkthrough.docx`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg bg-[#15110d] text-[#ff6900] text-sm font-semibold hover:opacity-90 flex items-center gap-1.5"
            data-testid="zoho-bridge-walkthrough-btn"
            title="Step-by-step click-by-click walkthrough · for Tanisha"
          >
            👣 Click-by-click walkthrough (.docx)
          </a>
          <a
            href={`${API}/xiaomi/admin/zoho-macro-guide.docx`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg bg-[#ff6900] text-white text-sm font-semibold hover:opacity-90 flex items-center gap-1.5"
            data-testid="zoho-bridge-macro-guide-btn"
            title="Word document · 4 Zoho macros explained for Tanisha"
          >
            📘 Macro setup guide (.docx)
          </a>
          <button
            onClick={sendRoutingTest}
            disabled={testSending}
            className="px-4 py-2 rounded-lg bg-white border-2 border-[#ff6900] text-[#ff6900] text-sm font-semibold hover:bg-[#fff3ea] disabled:opacity-50 flex items-center gap-1.5"
            data-testid="zoho-bridge-routing-test-btn"
            title="Creates one bare ticket in the Xiaomi Platinum Club department so Tanisha can confirm routing"
          >
            {testSending ? '⏳ Sending…' : '🎯 Send routing test ticket'}
          </button>
          <button
            onClick={() => load(true)}
            className="px-4 py-2 rounded-lg bg-[#15110d] text-[#f3e6cf] text-sm hover:opacity-90"
            data-testid="zoho-bridge-refresh-btn"
          >
            ↻ Refresh now
          </button>
        </div>
      </div>

      {/* Routing test result */}
      {testTicket && (
        <div
          className="rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] px-5 py-4"
          data-testid="zoho-bridge-routing-test-result"
        >
          <div className="text-sm font-semibold text-[#065f46]">
            ✓ Test ticket #{testTicket.ticket_number || testTicket.ticket_id} landed in department{' '}
            <code className="bg-white px-1.5 py-0.5 rounded">{testTicket.department_id}</code>
          </div>
          <div className="text-[13px] text-[#047857] mt-1">
            {testTicket.subject} ·{' '}
            <a
              href={testTicket.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
              data-testid="zoho-bridge-routing-test-link"
            >
              Open in Zoho Desk →
            </a>{' '}
            Confirm it shows under Xiaomi Platinum Club, then close it.
          </div>
        </div>
      )}

      {/* Top-line callout when bridge isn't live yet */}
      {!isGreen && (
        <div
          className="rounded-lg border border-[#fcd34d] bg-[#fef3c7] px-5 py-4"
          data-testid="zoho-bridge-pre-launch-banner"
        >
          <div className="text-sm font-semibold text-[#92400e]">
            ⏳ Bridge not yet live · awaiting Aditya's Zoho provisioning
          </div>
          <div className="text-[13px] text-[#78350f] mt-1">
            Once <code className="bg-white px-1.5 py-0.5 rounded">
              ZOHO_XIAOMI_DEPARTMENT_ID
            </code> is set in <code className="bg-white px-1.5 py-0.5 rounded">
              backend/.env
            </code> and backend restarts, this page flips green automatically.
            Placeholder currently:{' '}
            <code className="bg-white px-1.5 py-0.5 rounded">
              {h.department_id_placeholder || '(empty)'}
            </code>
          </div>
        </div>
      )}

      {/* 4 Health pills */}
      <section data-testid="zoho-bridge-health-section">
        <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
          Health
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <HealthPill
            testId="health-bridge-configured"
            label="Bridge configured"
            ok={h.bridge_configured}
            sub={h.bridge_configured ? 'OAuth + Dept ID both set' : 'Awaiting Dept ID'}
          />
          <HealthPill
            testId="health-dept-id"
            label="Department ID"
            ok={h.department_id_set}
            sub={h.department_id_set ? 'Set in env' : 'Placeholder still in env'}
          />
          <HealthPill
            testId="health-oauth"
            label="Zoho OAuth"
            ok={h.zoho_oauth_configured}
            sub={h.zoho_oauth_configured ? 'Tokens active' : 'Reconnect needed'}
          />
          <HealthPill
            testId="health-fallback"
            label="Fallback email"
            ok={!!h.fallback_email}
            sub={h.fallback_email}
          />
        </div>
      </section>

      {/* 4 Counts */}
      <section data-testid="zoho-bridge-counts-section">
        <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
          Open instance tickets
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CountStat
            testId="count-open-total"
            label="Open instances"
            value={c.open_instances_total}
            accent="ink"
          />
          <CountStat
            testId="count-with-ticket"
            label="With Zoho ticket"
            value={c.with_zoho_ticket}
            accent="teal"
          />
          <CountStat
            testId="count-awaiting"
            label="Awaiting ticket"
            value={c.awaiting_ticket}
            accent={c.awaiting_ticket > 0 ? 'amber' : 'ink'}
          />
          <CountStat
            testId="count-failed"
            label="Attempt failed"
            value={c.ticket_attempt_failed}
            accent={c.ticket_attempt_failed > 0 ? 'red' : 'ink'}
          />
        </div>
      </section>

      {/* Stuck instances table */}
      <section data-testid="zoho-bridge-stuck-section">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider text-soft">
            Stuck instances {stuck.length > 0 && `· showing first ${stuck.length}`}
          </div>
        </div>
        {stuck.length === 0 ? (
          <div
            className="rounded-lg border border-line bg-white p-6 text-sm text-soft"
            data-testid="zoho-bridge-stuck-empty"
          >
            ✓ No open instances awaiting Zoho ticket creation. Every open
            workflow has a workbench ticket.
          </div>
        ) : (
          <div
            className="rounded-lg border border-line bg-white overflow-x-auto"
            data-testid="zoho-bridge-stuck-table"
          >
            <table className="w-full text-sm">
              <thead className="bg-[#faf6ef] border-b border-line">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-soft">Retailer</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-soft">Outlet</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-soft">Benefit</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-soft">Tier · Zone</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-soft">Step</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-soft">Last attempt</th>
                </tr>
              </thead>
              <tbody>
                {stuck.map((s) => (
                  <tr
                    key={s.instance_id}
                    className="border-b border-line last:border-b-0 hover:bg-[#faf6ef]"
                    data-testid={`stuck-row-${s.instance_id}`}
                  >
                    <td className="px-4 py-2 font-mono text-[12px]">{s.dealer_id}</td>
                    <td className="px-4 py-2">{s.outlet_name}</td>
                    <td className="px-4 py-2">{s.benefit_name}</td>
                    <td className="px-4 py-2 text-[12px]">
                      {s.tier} · {s.zone}
                    </td>
                    <td className="px-4 py-2 text-[12px]">
                      {s.current_step}
                      {s.current_stage_key && (
                        <span className="text-soft"> · {s.current_stage_key}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[12px]">
                      {s.last_attempt_error ? (
                        <span className="text-[#b91c1c]">
                          ✕ {s.last_attempt_error}
                        </span>
                      ) : (
                        <span className="text-soft">not yet attempted</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Reference panel · cf_keys + doctrines */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="zoho-bridge-reference-section">
        <div className="rounded-lg border border-line bg-white p-5">
          <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
            Custom field map (8)
          </div>
          <ul className="text-[13px] space-y-1 font-mono">
            {(h.cf_field_keys || []).map((k) => (
              <li key={k} className="text-[#15110d]">
                {k === 'cf_concierge_directive' ? (
                  <span>
                    <span className="text-[#ff6900] mr-1">📌</span>
                    {k}
                    <span className="text-soft text-[11px] font-sans ml-2">
                      · pinned at top of ticket UI
                    </span>
                  </span>
                ) : k}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-line bg-white p-5">
          <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
            Sprint timeline
          </div>
          <ul className="text-[13px] space-y-1.5">
            {(data.doctrine_notes || []).map((n, i) => (
              <li key={i} className="text-[#374151]">· {n}</li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
